import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';

import toastConfig from '../components/shared/constants/toaster-config';
import { getConversationForRealtime } from '../utils/getConversations';
import { isRestrictedAgent } from '../utils/conversationLimit/roles';
import { unseatReasonLabel } from '../utils/conversationLimit/capacity';
import { playSeatSound } from '../utils/conversationLimit/seatSound';
import { updateConverations } from './conversationsDuck';
import { conversationLimitActions as A } from './conversationLimitReducer';
import { removeConversationLocallyAction } from './conversationLimitListActions';

/**
 * Conversation limit ("modo cola") — Pusher handlers (spec §6), dispatched
 * from DashboardLayout's `notificationActions`. All of them do nothing when
 * the feature is off, and none of them throws on a malformed payload.
 */

/** Unseat reasons after which the conversation belongs to another agent. */
const NO_LONGER_OWNED = ['reassigned', 'swept'];

const limitState = (getState) => getState().conversationLimit || {};
const isEnabled = (getState) => limitState(getState).enabled === true;
const clientIdOf = (data) => (data && typeof data.clientId === 'string' && data.clientId) || null;
const isNonEmpty = (value) => typeof value === 'string' && value !== '';
const hasSearchText = (getState) => {
  const search = getState().conversations?.searchFilter;
  return typeof search === 'string' && search.trim() !== '';
};
const isInList = (getState, clientId) => getState().conversations.data.some((c) => c.id === clientId);

/**
 * Fetches a seated conversation by id and puts it at the top of the inbox
 * through the same path as a `newClient` event. Shared by the
 * `conversationSeated` handler and the chat load's late merge. While
 * searching, the list shows search results; the seat shows up once the search
 * is cleared and the inbox reloads. A seat the inbox filters reject (an
 * archived one under «Todo») stays out of the list, and the open conversation
 * stays open (`insertOnly`, spec S16).
 */
export const insertSeatedConversationAction = (clientId) => async (dispatch, getState) => {
  if (hasSearchText(getState) || isInList(getState, clientId)) return;
  try {
    const payload = await getConversationForRealtime(clientId);
    // Re-checked after the request: a `newMessage` for this seat may have
    // inserted the row meanwhile, and replaying the fetched copy over it would
    // count the same inbound message as unread twice.
    if (!payload || isInList(getState, clientId)) return;
    await dispatch(updateConverations(payload, { insertOnly: true }));
  } catch (error) {
    Sentry.captureException(error, { tags: { component: 'conversationLimit', op: 'conversationSeated' } });
  }
};

/** `{clientId, seatIndex, source}` on the agent channel: "le llega una nueva". */
export const conversationSeatedReceived = (data) => async (dispatch, getState) => {
  const clientId = clientIdOf(data);
  if (!isEnabled(getState) || !clientId) return;

  const isDuplicate = limitState(getState).seatIds.includes(clientId);
  dispatch(A.seatAdded(clientId));
  if (!isDuplicate) {
    playSeatSound();
    toast.info('Tienes una nueva conversación asignada.', toastConfig);
  }
  await dispatch(insertSeatedConversationAction(clientId));
};

/**
 * `{clientId, reason}`: closed, released, reassigned, idle, swept. The notice
 * is for a seat somebody ELSE took away (an admin, the idle sweeper, another
 * user closing it). When this tab caused it — the agent ended or released the
 * chat — the action already gave its own feedback, so nothing is shown.
 */
export const conversationUnseatedReceived = (data) => async (dispatch, getState) => {
  const clientId = clientIdOf(data);
  if (!isEnabled(getState) || !clientId) return;

  // Read before the seat goes: removing it also drops its own-action mark.
  const takenByOthers = !limitState(getState).ownUnseatIds.includes(clientId);
  dispatch(A.seatRemoved(clientId));
  if (takenByOthers && getState().conversations.current?.id === clientId) {
    toast.warning(`Esta conversación ${unseatReasonLabel(data.reason)} y salió de tu cupo.`, toastConfig);
  }
  const restricted = isRestrictedAgent(getState().user?.data);
  // Reassigned or swept by somebody else: the conversation is no longer this
  // agent's, so the open chat closes too — otherwise the composer stays enabled
  // on a conversation that now belongs to someone else. After `closed` or
  // `idle` it is still theirs, so the open chat stays.
  if (restricted && takenByOthers && NO_LONGER_OWNED.includes(data.reason)) {
    dispatch(removeConversationLocallyAction(clientId, { closeIfOpen: true }));
    return;
  }
  if (restricted && !hasSearchText(getState)) dispatch(removeConversationLocallyAction(clientId));
};

/** `{activeCount, limit, status}` on the agent channel. */
export const capacityReceived = (data) => (dispatch) => {
  dispatch(A.capacityReceived(data));
};

/** `{waiting}` on the company channel (admins): queue badge (R6) and the "Carga por agente" queue. */
export const queueChangedReceived = (data) => (dispatch) => {
  dispatch(A.queueChanged(data && typeof data === 'object' ? { ...data, receivedAt: new Date().toISOString() } : data));
};

/**
 * `{agentId, activeCount, limit, status, at}` on the company channel (admins):
 * one agent's load, so the open "Carga por agente" updates that row, and the
 * admin's own "N de L" when it is theirs. Backends without it never send it.
 */
export const agentLoadReceived = (data) => (dispatch, getState) => {
  const ownId = getState().user?.data?.id;
  dispatch(A.agentLoadReceived(data, !!data && isNonEmpty(ownId) && data.agentId === ownId));
};
